import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DocumentArrowDownIcon,
  PrinterIcon, // Add PrinterIcon
  ClipboardDocumentCheckIcon,
  CalendarDaysIcon,
  VideoCameraIcon,
  WrenchScrewdriverIcon
} from '@heroicons/react/24/outline';
import axios from '../../api/axiosConfig';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';

interface WorkOrderStats {
  id: number;
  workOrderNumber: string;
  type: string;
  clientName: string;
  orderNumber: string;
  structureCode: string;
  structureLocation: string;
  assignedToName: string;
  status: string;
  scheduledDate: string;
  completedDate: string;
  completionTime: number;
}

const statusColors: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  inProgress: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
  delayed: 'bg-orange-100 text-orange-800'
};

const statusLabels: Record<string, string> = {
  pending: 'Ожидает',
  inProgress: 'В процессе',
  completed: 'Выполнен',
  cancelled: 'Отменен',
  delayed: 'Отложен'
};

const typeLabels: Record<string, string> = {
  installation: 'Установка',
  removal: 'Демонтаж',
  maintenance: 'Обслуживание',
  videoReport: 'Видеоотчет'
};

const WorkOrderReport: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [workOrderStats, setWorkOrderStats] = useState<WorkOrderStats[]>([]);
  const [period, setPeriod] = useState('month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortBy, setSortBy] = useState('scheduledDate');
  const [sortOrder, setSortOrder] = useState('desc');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Set default date range if not selected
        const end = new Date();
        const start = new Date();
        
        if (period === 'month') {
          start.setMonth(end.getMonth() - 1);
        } else if (period === 'quarter') {
          start.setMonth(end.getMonth() - 3);
        } else if (period === 'year') {
          start.setFullYear(end.getFullYear() - 1);
        }
        
        const formattedStartDate = startDate || start.toISOString().split('T')[0];
        const formattedEndDate = endDate || end.toISOString().split('T')[0];
        
        // Fetch work order statistics
        const response = await axios.get('/reports/workorders', {
          params: {
            startDate: formattedStartDate,
            endDate: formattedEndDate,
            sortBy,
            sortOrder,
            status: statusFilter !== 'all' ? statusFilter : undefined,
            type: typeFilter !== 'all' ? typeFilter : undefined
          }
        });
        
        setWorkOrderStats(response.data);
        
        // Update date inputs with actual dates used
        if (!startDate) setStartDate(formattedStartDate);
        if (!endDate) setEndDate(formattedEndDate);
      } catch (error) {
        console.error('Error fetching work order report data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [period, startDate, endDate, sortBy, sortOrder, statusFilter, typeFilter]); // Added startDate and endDate dependencies
  
  const handlePeriodChange = (newPeriod: string) => {
    setPeriod(newPeriod);
    setStartDate('');
    setEndDate('');
  };
  
  const handleDateRangeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Update state, useEffect will trigger fetch
    if (startDate && endDate) {
      setPeriod('custom' + startDate + endDate);
    }
  };

  // Removed separate fetchWorkOrderStats function
  
  const handleSortChange = (column: string) => {
    if (sortBy === column) {
      // Toggle sort order if the same column
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new sort column and default to descending
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  // Open printable view in a new tab
  const handleOpenPrintView = () => {
    if (!startDate || !endDate) {
      alert('Пожалуйста, выберите или примените период для печати отчета.');
      return;
    }
    // Construct the URL for the print page with query parameters
    const params = new URLSearchParams({ startDate, endDate });
    if (statusFilter !== 'all') {
      params.append('status', statusFilter);
    }
    if (typeFilter !== 'all') {
      params.append('type', typeFilter);
    }
    const printUrl = `/reports/workorders/print?${params.toString()}`;
    // Open the URL in a new tab
    const printWindow = window.open(printUrl, '_blank');
    if (!printWindow) {
      alert('Не удалось открыть окно для печати. Пожалуйста, проверьте настройки блокировки всплывающих окон.');
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    if (!dateString) return 'Нет данных';
    return new Date(dateString).toLocaleDateString('ru-KZ');
  };
  
  // Count stats
  const totalWorkOrders = workOrderStats.length;
  const completedWorkOrders = workOrderStats.filter(wo => wo.status === 'completed').length;
  const installationOrders = workOrderStats.filter(wo => wo.type === 'placement').length;
  const videoReportOrders = workOrderStats.filter(wo => wo.type === 'videoReport').length;
  
  // Calculate completion percentage
  const completionRate = totalWorkOrders > 0 
    ? Math.round((completedWorkOrders / totalWorkOrders) * 100) 
    : 0;
  
  if (loading) {
    return <LoadingSpinner />;
  }
  
  return (
    <div>
      <PageHeader
        title="Отчет по нарядам"
        subtitle={`Статистика по нарядам за период: ${startDate ? new Date(startDate).toLocaleDateString('ru-KZ') : '?'} - ${endDate ? new Date(endDate).toLocaleDateString('ru-KZ') : '?'}`}
        actions={
          <button
            onClick={handleOpenPrintView} // Use new handler
            className="btn-secondary flex items-center"
            disabled={!startDate || !endDate} // Disable if dates aren't set
          >
            <PrinterIcon className="h-5 w-5 mr-1" /> {/* Use PrinterIcon */}
            Печать
          </button>
        }
      />
      
      {/* Period selection */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4 justify-between mb-4">
          <div className="flex space-x-2">
            <button
              className={`px-3 py-1 rounded ${period === 'month' ? 'bg-primary text-white' : 'bg-gray-100'}`}
              onClick={() => handlePeriodChange('month')}
            >
              Месяц
            </button>
            <button
              className={`px-3 py-1 rounded ${period === 'quarter' ? 'bg-primary text-white' : 'bg-gray-100'}`}
              onClick={() => handlePeriodChange('quarter')}
            >
              Квартал
            </button>
            <button
              className={`px-3 py-1 rounded ${period === 'year' ? 'bg-primary text-white' : 'bg-gray-100'}`}
              onClick={() => handlePeriodChange('year')}
            >
              Год
            </button>
          </div>
          
          <div className="flex space-x-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="form-input"
            >
              <option value="all">Все статусы</option>
              <option value="pending">Ожидает</option>
              <option value="inProgress">В процессе</option>
              <option value="completed">Выполнен</option>
              <option value="cancelled">Отменен</option>
              <option value="delayed">Отложен</option>
            </select>
            
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="form-input"
            >
              <option value="all">Все типы</option>
              <option value="installation">Установка</option>
              <option value="removal">Демонтаж</option>
              <option value="maintenance">Обслуживание</option>
              <option value="videoReport">Видеоотчет</option>
            </select>
          </div>
        </div>
        
        <form onSubmit={handleDateRangeSubmit} className="flex flex-col sm:flex-row gap-2 items-end">
          <div className="w-full sm:w-auto">
            <label className="block text-sm font-medium text-gray-700 mb-1">Начальная дата</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="form-input"
              required
            />
          </div>
          <div className="w-full sm:w-auto">
            <label className="block text-sm font-medium text-gray-700 mb-1">Конечная дата</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="form-input"
              required
            />
          </div>
          <button type="submit" className="btn-primary flex items-center">
            <CalendarDaysIcon className="h-5 w-5 mr-1" />
            Применить
          </button>
        </form>
      </div>
      
      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Всего нарядов</p>
              <p className="text-2xl font-bold text-gray-900">{totalWorkOrders}</p>
            </div>
            <div className="bg-purple-100 p-3 rounded-full">
              <ClipboardDocumentCheckIcon className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Выполнено</p>
              <p className="text-2xl font-bold text-green-700">{completionRate}%</p>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <ClipboardDocumentCheckIcon className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Установка</p>
              <p className="text-2xl font-bold text-gray-900">{installationOrders}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <WrenchScrewdriverIcon className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Видеоотчеты</p>
              <p className="text-2xl font-bold text-gray-900">{videoReportOrders}</p>
            </div>
            <div className="bg-orange-100 p-3 rounded-full">
              <VideoCameraIcon className="h-6 w-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>
      
      {/* Work orders table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 table-fixed">
            <thead className="bg-gray-50">
              <tr>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSortChange('workOrderNumber')}
                >
                  Номер наряда
                  {sortBy === 'workOrderNumber' && (
                    <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSortChange('type')}
                >
                  Тип
                  {sortBy === 'type' && (
                    <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSortChange('scheduledDate')}
                >
                  Запланирован
                  {sortBy === 'scheduledDate' && (
                    <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSortChange('status')}
                >
                  Статус
                  {sortBy === 'status' && (
                    <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSortChange('structureCode')}
                >
                  Конструкция
                  {sortBy === 'structureCode' && (
                    <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSortChange('assignedToName')}
                >
                  Исполнитель
                  {sortBy === 'assignedToName' && (
                    <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSortChange('completedDate')}
                >
                  Выполнен
                  {sortBy === 'completedDate' && (
                    <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                  )}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {workOrderStats.length > 0 ? (
                workOrderStats.map((workOrder) => (
                  <tr 
                    key={workOrder.id} 
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/work-orders/${workOrder.id}`)}
                  >
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      {workOrder.workOrderNumber}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {typeLabels[workOrder.type] || workOrder.type}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {formatDate(workOrder.scheduledDate)}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${statusColors[workOrder.status] || 'bg-gray-100 text-gray-800'}`}>
                        {statusLabels[workOrder.status] || workOrder.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {workOrder.structureCode}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {workOrder.assignedToName}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {workOrder.completedDate ? formatDate(workOrder.completedDate) : '—'}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500">
                    Нет данных для отображения
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default WorkOrderReport;
